export const SLIDE_WIDTH = 1920;
export const SLIDE_HEIGHT = 1080;

export const PAGE_FORMATS = ['widescreen_16_9', 'a4_portrait'] as const;
export type PageFormat = (typeof PAGE_FORMATS)[number];
export const DEFAULT_PAGE_FORMAT: PageFormat = 'widescreen_16_9';

export interface SlideSize {
  width: number;
  height: number;
  exportWidth: number;
  exportHeight: number;
  aspectRatio: string;
  pdfWidth: string;
  pdfHeight: string;
  printDpi: number;
}

const SLIDE_SIZES: Record<PageFormat, SlideSize> = {
  widescreen_16_9: {
    width: SLIDE_WIDTH,
    height: SLIDE_HEIGHT,
    exportWidth: SLIDE_WIDTH,
    exportHeight: SLIDE_HEIGHT,
    aspectRatio: '16 / 9',
    pdfWidth: '1920px',
    pdfHeight: '1080px',
    printDpi: 72,
  },
  a4_portrait: {
    width: 1080,
    height: 1528,
    exportWidth: 2480,
    exportHeight: 3508,
    aspectRatio: '210 / 297',
    pdfWidth: '210mm',
    pdfHeight: '297mm',
    printDpi: 300,
  },
};

export function resolvePageFormat(format?: string | null): PageFormat {
  return format === 'a4_portrait' ? 'a4_portrait' : DEFAULT_PAGE_FORMAT;
}

export function slideSize(format?: string | null): SlideSize {
  return SLIDE_SIZES[resolvePageFormat(format)];
}

export function pageFormatsMatch(left?: string | null, right?: string | null): boolean {
  return resolvePageFormat(left) === resolvePageFormat(right);
}

export function templatesMatchingPageFormat<T extends { pageFormat?: PageFormat | string | null }>(
  templates: T[],
  pageFormat?: string | null,
): T[] {
  return templates.filter((template) => pageFormatsMatch(template.pageFormat, pageFormat));
}

export function resolvedLineHeight(style?: { lineHeight?: number; fontFamily?: string }): number {
  if (style?.lineHeight != null && style.lineHeight >= 0.7) return style.lineHeight;
  if (style?.fontFamily === 'Kapra Neue') return 1;
  return 1.2;
}
export const ALLOWED_FONTS = ['Kapra Neue', 'Akkurat Std', 'Inter', 'Arial', 'Oswald'] as const;
export const SIMPLE_FONTS = [
  { id: 'kapra-condensed-semibold', family: 'Kapra Neue', label: 'Kapra Neue Condensed Semibold', fontWeight: 600 },
  { id: 'kapra-regular', family: 'Kapra Neue', label: 'Kapra Neue Regular', fontWeight: 400 },
  { id: 'akkurat-light', family: 'Akkurat Std', label: 'Akkurat Light', fontWeight: 300 },
  { id: 'akkurat-regular', family: 'Akkurat Std', label: 'Akkurat Regular', fontWeight: 400 },
  { id: 'akkurat-bold', family: 'Akkurat Std', label: 'Akkurat Bold', fontWeight: 700 },
] as const;

export type SimpleFont = (typeof SIMPLE_FONTS)[number];

export function isBodyFont(family?: string): boolean {
  return !family || family === 'Akkurat Std' || family === 'Inter';
}

export function defaultFontWeight(family?: string): number {
  if (family === 'Kapra Neue') return 600;
  if (isBodyFont(family)) return 300;
  return 400;
}

export function resolvedFontWeight(style?: { fontFamily?: string; fontWeight?: number | string }): number {
  if (style?.fontWeight != null && String(style.fontWeight) !== '') return Number(style.fontWeight);
  return defaultFontWeight(style?.fontFamily);
}

export function resolveSlideFontFamily(family?: string): string {
  if (family === 'Kapra Neue') return '"Kapra Neue", Oswald, sans-serif';
  if (isBodyFont(family)) return '"Akkurat Std", Inter, Arial, sans-serif';
  return `${family}, Arial, sans-serif`;
}

export function isSimpleFontActive(
  style: { fontFamily?: string; fontWeight?: number | string } | undefined,
  font: Pick<SimpleFont, 'family' | 'fontWeight'>
): boolean {
  const family = style?.fontFamily;
  const familyMatch = font.family === 'Akkurat Std' ? isBodyFont(family) : family === font.family;
  if (!familyMatch) return false;
  return resolvedFontWeight(style) === font.fontWeight;
}

export function matchSimpleFont(
  style: { fontFamily?: string; fontWeight?: number | string } | undefined,
): SimpleFont | null {
  return SIMPLE_FONTS.find((font) => isSimpleFontActive(style, font)) || null;
}

export const FONTANA_SLIDE_COLORS = [
  { hex: '#1B3967', rgb: [27, 57, 103], name: 'Olympia', group: 'primary' as const },
  { hex: '#998A6B', rgb: [153, 138, 107], name: 'Sand', group: 'primary' as const },
  { hex: '#EDE8DC', rgb: [237, 232, 220], name: 'Sand ljus', group: 'primary' as const },
  { hex: '#F5F0E8', rgb: [245, 240, 232], name: 'Sand extra ljus', group: 'primary' as const },
  { hex: '#0050E6', rgb: [0, 80, 230], name: 'Originalet', group: 'primary' as const },
  { hex: '#FFFFFF', rgb: [255, 255, 255], name: 'Vit', group: 'primary' as const },
  { hex: '#111111', rgb: [17, 17, 17], name: 'Svart', group: 'primary' as const },
  { hex: '#D94000', rgb: [217, 64, 0], name: 'Hetta', group: 'complement' as const },
  { hex: '#4DCCA6', rgb: [77, 204, 166], name: 'Hav', group: 'complement' as const },
  { hex: '#9FC300', rgb: [159, 195, 0], name: 'Gräs', group: 'complement' as const },
  { hex: '#9933B3', rgb: [153, 51, 179], name: 'Bougainvillea', group: 'accent' as const },
  { hex: '#33BF00', rgb: [51, 191, 0], name: 'Ekologisk', group: 'accent' as const },
];

export const ALLOWED_COLORS = FONTANA_SLIDE_COLORS.map((color) => color.hex);

export type PresentationMarket = 'SE' | 'FI' | 'INT';
export type PresentationLanguage = 'sv' | 'fi' | 'en';
export type PresentationStatus = 'draft' | 'review' | 'ready';
export type SlideObjectType = 'text' | 'image' | 'shape' | 'product' | 'table' | 'chart';
export type ChartKind = 'column' | 'bar' | 'line' | 'pie';
export type ChartBackground = 'none' | 'white' | 'sand';

export interface ChartSeries {
  id: string;
  name: string;
  colorId: string;
  values: number[];
}

export interface ChartData {
  kind: ChartKind;
  categories: string[];
  series: ChartSeries[];
  showLegend: boolean;
  showValues: boolean;
  background: ChartBackground;
  categoryColorIds: string[];
}

export interface TableCell {
  id: string;
  content: string;
}

export interface TableData {
  headerRow: boolean;
  colWidths: number[];
  rowHeights?: number[];
  rows: Array<{ cells: TableCell[] }>;
}
export type TrackingMode = 'trackLatest' | 'pin';
export type ConversionState = 'native' | 'partial' | 'raster';
export type LinkedFieldKey = string;
export type LinkedFieldSource = 'direct' | 'local';
export type FieldBindingKind = 'logical' | 'raw';

export interface FieldBinding {
  kind: FieldBindingKind;
  key: string;
}

export interface LinkedField {
  key: LinkedFieldKey;
  source: LinkedFieldSource;
  value: string;
}

export interface LinkedFrom {
  objectId: string;
  key: LinkedFieldKey;
}

export interface ObjectLocks {
  position: boolean;
  size: boolean;
  delete: boolean;
  content: boolean;
  style: boolean;
  crop: boolean;
  animation: boolean;
}

export interface SlideObjectStyle {
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: number | string;
  color?: string;
  backgroundColor?: string;
  textAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  lineHeight?: number;
  letterSpacing?: number;
  textTransform?: 'none' | 'uppercase' | 'lowercase';
  opacity?: number;
  fontStyle?: 'normal' | 'italic';
  textDecoration?: 'none' | 'underline';
  shape?: 'rect' | 'roundRect' | 'ellipse' | 'triangle' | 'line';
  strokeColor?: string;
  strokeWidth?: number;
  strokeDash?: 'solid' | 'dashed' | 'dotted';
  objectFit?: 'cover' | 'contain';
  textFit?: 'hug' | 'box';
  rotation?: number;
  cornerRadius?: number;
  padding?: number;
  cropLeft?: number;
  cropTop?: number;
  cropRight?: number;
  cropBottom?: number;
  shadow?: ObjectShadow;
  blendMode?: 'normal' | 'multiply';
}

export interface ObjectShadow {
  enabled: boolean;
  color: string;
  opacity: number;
  angle: number;
  distance: number;
  blur: number;
  spread: number;
}

export interface SlideObject {
  id: string;
  type: SlideObjectType;
  name?: string;
  x: number;
  y: number;
  w: number;
  h: number;
  z: number;
  locks: ObjectLocks;
  groupId?: string | null;
  assetId?: string | null;
  assetVersionId?: string | null;
  trackingMode?: TrackingMode;
  content?: string;
  style?: SlideObjectStyle;
  conversionState?: ConversionState;
  imageUrl?: string;
  linkedFields?: LinkedField[];
  linkedFrom?: LinkedFrom;
  globalComponentId?: string | null;
  detached?: boolean;
  slotId?: string | null;
  layoutObjectId?: string;
  fieldBinding?: FieldBinding;
  table?: TableData;
  chart?: ChartData;
}

export interface PresentationSlide {
  slideId: string;
  presentationId?: string;
  order: number;
  layoutId: string;
  objects: SlideObject[];
  notes?: string;
  sectionId?: string | null;
  sectionTitle?: string | null;
  conversionProgress?: number;
  backgroundColor?: string;
  hidden?: boolean;
  locked?: boolean;
  librarySlideId?: string | null;
  libraryDetached?: boolean;
}

export interface PresentationFamilyMember {
  _id: string;
  name: string;
  language: PresentationLanguage;
  market: PresentationMarket;
  status: PresentationStatus;
  pageFormat?: PageFormat | string;
}

export interface PresentationRecord {
  _id: string;
  name: string;
  ownerUserId: string;
  templateId?: string | null;
  familyId?: string | null;
  familyMembers?: PresentationFamilyMember[];
  market: PresentationMarket;
  language: PresentationLanguage;
  status: PresentationStatus;
  currentRevision: number;
  classification: 'internal' | 'customer' | 'confidential';
  deckCategoryId?: string | null;
  pageFormat?: PageFormat;
  createdAt?: string;
  updatedAt?: string;
  slides?: PresentationSlide[];
  coverSlideId?: string | null;
  slideCount?: number;
  mediaUpdates?: MediaUpdates;
  libraryUpdates?: LibrarySlideUpdate[];
  originReview?: OriginReview;
  originReviewPending?: boolean;
  libraryUpdatePending?: boolean;
  aiEnabled?: boolean;
  aiGenerateEnabled?: boolean;
  generatedByAi?: boolean;
  generationRunId?: string | null;
}

export type OriginReviewKind = 'text' | 'image' | 'table' | 'chart' | 'page';

export interface OriginReviewItem {
  slideId: string;
  objectId: string;
  kind: OriginReviewKind;
  pageIndex: number;
  signature: string;
  originalPreview?: string;
  currentPreview?: string;
  libraryFollow?: boolean;
}

export interface OriginReview {
  originalId: string;
  originalName: string;
  originalLanguage: PresentationLanguage;
  originalMarket: PresentationMarket;
  items: OriginReviewItem[];
}

export interface LibrarySlideUpdate {
  slideId: string;
  index: number;
  libraryName?: string;
  backgroundChanged: boolean;
  objectsChanged: boolean;
  sourceMissing: boolean;
}

export interface MediaUpdateItem {
  slideId: string;
  objectId: string;
  fromVersion: string | null;
  toVersion: string | null;
  title: string;
}

export interface MediaUpdates {
  applied: MediaUpdateItem[];
  available: MediaUpdateItem[];
}

export interface PresentationComment {
  _id: string;
  presentationId: string;
  slideId: string;
  objectId?: string | null;
  body: string;
  authorUserId: string;
  createdAt: string;
  resolvedAt?: string | null;
}

export const DECK_CATEGORY_KINDS = ['presentation', 'salesSheet'] as const;
export type DeckCategoryKind = (typeof DECK_CATEGORY_KINDS)[number];
export type PresentationCatalog = 'presentations' | 'salesSheets';

export interface PresentationDeckCategory {
  _id: string;
  name: string;
  parentCategory?: string | null;
  order: number;
  kind?: DeckCategoryKind;
}

export interface PresentationGlobalComponent {
  _id: string;
  name: string;
  objects: SlideObject[];
  version: number;
}

export const LIBRARY_SLIDE_GROUPS = ['product', 'price', 'agenda', 'other'] as const;
export type LibrarySlideGroup = (typeof LIBRARY_SLIDE_GROUPS)[number];

export interface PresentationLibraryLabel {
  _id: string;
  name: string;
  legacyKey?: LibrarySlideGroup;
}

export interface PresentationLibrarySlide {
  _id: string;
  name: string;
  pageFormat?: PageFormat;
  backgroundColor?: string;
  objects: SlideObject[];
  version: number;
  isActive?: boolean;
  group?: LibrarySlideGroup;
  labelIds?: string[];
  createdByUserId?: string;
}

export interface PresentationTemplate {
  _id: string;
  name: string;
  version: number;
  isActive?: boolean;
  pageFormat?: PageFormat;
  layouts: Array<{ layoutId: string; name: string; objects: SlideObject[]; backgroundColor?: string }>;
  allowedFonts: string[];
  allowedColors: string[];
}

export interface HealthIssue {
  severity: 'blocker' | 'warning';
  code: string;
  message: string;
  slideId: string;
  objectId?: string;
}

export interface VisibleAsset {
  _id: string;
  title: string;
  fileName: string;
  previewUrl?: string;
  fileType?: string;
  kontor?: boolean;
  currentVersionId?: string;
  articleNumber?: string;
  measure?: string;
  metaLine?: string;
}

export interface VisibleAssetPage {
  items: VisibleAsset[];
  total: number;
  skip: number;
  limit: number;
}

export interface PresentationAssetDetail extends VisibleAsset {
  tags: string[];
  categories: Array<{ _id: string; name: string }>;
  versions: Array<{ versionId: string; createdAt?: string; note?: string }>;
  metadata?: Record<string, unknown>;
  placeable: boolean;
  placeKind: 'image' | 'product' | null;
  unsupportedReason?: string;
  product?: {
    articleNumber?: string;
    name?: string;
  };
}

export type LibraryIntent = 'insert-image' | 'insert-product' | 'insert-icon' | 'replace';

export interface PresentationRevision {
  _id: string;
  revision: number;
  cause: 'autosave' | 'snapshot' | 'ready' | 'restore';
  createdAt: string;
  snapshot?: unknown;
}

export type ImportIssueKind = 'unlinked_image' | 'missing_media' | 'unsupported' | 'unplaced_image';

export interface ImportIssue {
  kind: ImportIssueKind;
  slideIndex: number;
  detail: string;
}

export interface ImportSummary {
  slideCount: number;
  averageConversion: number;
  convertedBlocks: number;
  rasterBlocks: number;
  skippedAnimations: number;
  issues: ImportIssue[];
  slides: Array<{
    slideId: string;
    order: number;
    conversionProgress: number;
    detected: { text: number; image: number; shape: number; raster: number };
    issues?: ImportIssue[];
  }>;
}

export interface ImportPaletteColor {
  id: string;
  name: string;
  hex: string;
}

export interface ImportOffPaletteColor {
  hex: string;
  count: number;
}

export interface ImportAnalyzeResult {
  sessionId: string | null;
  offPaletteColors: ImportOffPaletteColor[];
  palette: ImportPaletteColor[];
  committed: boolean;
  slideCount?: number;
  averageConversion?: number;
  convertedBlocks?: number;
  rasterBlocks?: number;
  skippedAnimations?: number;
  issues?: ImportIssue[];
  slides?: ImportSummary['slides'];
}

export const defaultLocks = (overrides: Partial<ObjectLocks> = {}): ObjectLocks => ({
  position: false,
  size: false,
  delete: false,
  content: false,
  style: false,
  crop: false,
  animation: false,
  ...overrides,
});

export function createObjectId(prefix = 'obj'): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
